import React, { useCallback, useState } from 'react';
import { Loader } from '@mantine/core';
import { pickLocalFiles } from '@shakkerai/util-file';
import { translate } from '@shakkerai/util-i18n';
import { notifyError } from '@shakkerai/pro-components';
import { convert2CompressedUrl, getLocalImageSize } from '@shakkerai/util-image';
import { uploadImage } from '@shakkerai/pro-utils';
import { DragReceiver } from '@starflow/app/components/dragReceiver';

export const MAX_FILE_SIZE = 1024 * 1024 * 20;

export const Image = React.memo((props) => {
  const { title, badge, name, value, onChange } = props;
  const [uploading, setUploading] = useState(false);

  const handleUploadFile = useCallback(async () => {
    const [file] = await pickLocalFiles(['jpeg', 'jpg', 'png', 'webp']);

    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      notifyError(translate('common_operation_failed'), translate('img_size_toast'));
      return false;
    }

    setUploading(true);

    try {
      const result = await uploadImage(file, true);
      const size = await getLocalImageSize(file);

      if (result && result.url) {
        onChange(
          {
            width: size.width,
            height: size.height,
            url: result.url,
          },
          name,
        );
      } else {
        notifyError(translate('common_operation_failed'), translate('img_upload_failed'));
      }
    } catch (error) {
      notifyError(translate('common_operation_failed'), translate('img_upload_failed'));
    } finally {
      setUploading(false);
    }
  }, [name, onChange]);

  return (
    <div>
      {title && (
        <div className="mb-1">
          <span className="text-xs text-[#7f7f7f] leading-[14px]">{title}</span>
        </div>
      )}
      <DragReceiver name={name} onDrop={onChange}>
        {(isDraggingOver) => {
          return (
            <div
              className={`group flex flex-col items-center justify-center h-[188px] rounded overflow-hidden bg-[#f4f6f8] border ${
                isDraggingOver ? 'border-primary pointer-events-none' : 'border-[#ededed]'
              }`}
            >
              {badge && (
                <span className="absolute z-[3] top-1.5 left-1.5 px-3 py-1 bg-black/30 rounded text-xs text-white">
                  {badge}
                </span>
              )}
              {uploading ? (
                <>
                  <Loader size={16} />
                  <div className="mt-3 text-xs text-[#2d2d2d]">正在上传图片</div>
                </>
              ) : !value?.url ? (
                <button
                  onClick={handleUploadFile}
                  disabled={isDraggingOver}
                  className="w-32 h-[34px] bg-white border border-[#ededed] hover:border-[#dedede] rounded text-xs text-[#2d2d2d] disabled:pointer-events-none disabled:opacity-50"
                >
                  {translate('workflow_image_upload')}
                </button>
              ) : (
                <>
                  <img
                    crossOrigin="anonymous"
                    id="orginal-img-element"
                    className="w-full h-[186px] object-contain"
                    src={convert2CompressedUrl(value.url, 400)}
                  />
                  {!isDraggingOver && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded opacity-0 group-hover:opacity-100">
                      <button
                        onClick={handleUploadFile}
                        className="w-32 h-[34px] bg-white/5 border border-white/50 hover:bg-white/10 hover:border-white/80 rounded text-xs text-white"
                      >
                        {translate('workflow_image_upload')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }}
      </DragReceiver>
    </div>
  );
});

export const ImageView = React.memo(({ title, value: { url } }) => {
  return (
    <>
      <div className="p-4 m-auto">
        <h5 id="model-checkpoints-entry" className="text-[#2d2d2d] text-xs mb-3 leading-[14px] font-bold font-gro">
          {title}
        </h5>
        <img src={convert2CompressedUrl(url, 262)} />
      </div>
      <div className="border-t border-[#efefef]" />
    </>
  );
});
